import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";
import RecipeCard from "@/components/RecipeCard";
import { RECIPES } from "@/constants/testIds";

const TYPES = ["soup", "main", "side", "starter", "street", "drink", "dessert", "condiment"];
const DIFFS = ["easy", "medium", "hard"];

export default function RecipesPage() {
  const { lang } = useLang();
  const t = useDict(lang);
  const [params, setParams] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [countries, setCountries] = useState([]);

  const q = params.get("q") || "";
  const country = params.get("country") || "";
  const type = params.get("type") || "";
  const difficulty = params.get("difficulty") || "";

  const filters = useMemo(() => {
    const f = {};
    if (q) f.q = q;
    if (country) f.country = country;
    if (type) f.type = type;
    if (difficulty) f.difficulty = difficulty;
    return f;
  }, [q, country, type, difficulty]);

  useEffect(() => { api.countries().then(setCountries).catch(() => {}); }, []);
  useEffect(() => { api.listRecipes(filters).then(setRecipes).catch(() => {}); }, [filters]);

  const set = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };
  const clear = () => setParams(new URLSearchParams());

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 lg:pt-24">
      <p className="eyebrow mb-4">№ 02</p>
      <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl tracking-tight mb-12">{t.recipes.title}</h1>

      <div className="grid lg:grid-cols-12 gap-8 mb-12 items-end">
        <div className="lg:col-span-5 relative">
          <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
          <input
            data-testid={RECIPES.search}
            value={q}
            onChange={(e) => set("q", e.target.value)}
            placeholder={t.recipes.searchPlaceholder}
            className="input-line pl-7"
          />
        </div>
        <div className="lg:col-span-2">
          <span className="label-line">{t.recipes.filterCountry}</span>
          <select data-testid={RECIPES.countrySelect} value={country} onChange={(e) => set("country", e.target.value)} className="input-line">
            <option value="">{t.recipes.all}</option>
            {countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="lg:col-span-2">
          <span className="label-line">{t.recipes.filterType}</span>
          <select data-testid={RECIPES.typeSelect} value={type} onChange={(e) => set("type", e.target.value)} className="input-line">
            <option value="">{t.recipes.all}</option>
            {TYPES.map((ty) => <option key={ty} value={ty}>{t.types[ty]}</option>)}
          </select>
        </div>
        <div className="lg:col-span-2">
          <span className="label-line">{t.recipes.filterDifficulty}</span>
          <select data-testid={RECIPES.difficultySelect} value={difficulty} onChange={(e) => set("difficulty", e.target.value)} className="input-line">
            <option value="">{t.recipes.all}</option>
            {DIFFS.map((d) => <option key={d} value={d}>{t.difficulties[d]}</option>)}
          </select>
        </div>
        <div className="lg:col-span-1">
          <button data-testid={RECIPES.clearFilters} onClick={clear} className="text-xs uppercase tracking-[0.2em] underline">{t.recipes.clear}</button>
        </div>
      </div>

      {recipes.length === 0 ? (
        <p data-testid={RECIPES.empty} className="font-serif-display text-3xl italic text-[color:var(--ink-3)] py-20 text-center">{t.recipes.empty}</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 lg:gap-10 stagger">
          {recipes.map((r) => <RecipeCard key={r.slug} recipe={r} testId={RECIPES.card(r.slug)} />)}
        </div>
      )}
    </div>
  );
}
